import { toast } from "react-toastify"
import React, { useEffect, useState } from "react"

import { PEOPLE_PROFILE } from "../../api/Registry"
import { APPLICATION } from "../../utils/Constants"
import { generateUniqueId } from "../../utils/Helpers"
import HttpServices from "../../services/HttpServices"
import { PhaseBridge } from "../../lib/phases/PhaseBridge"
import { PhaseState, PhaseManifest } from "../../utils/Interfaces"
import { PhaseErrorState } from "../../lib/phases/PhaseErrorState"
import { SplitScreenModal } from "../../lib/fragments/SplitScreenModal"
import { ProfileDefinitionPhaser } from "./wizard/ProfileDefinitionPhaser"
import { G_onInputChangeHandler, G_ValidationLogic } from "../../components/utils/InputHandlers"
import { generateDefaultValue, getPhaseBucket, getPhaseDefinitions, initializePhasing, inputRegistryLookup, lookupElement, modifyElement, resolveManifestLogic } from "../../lib/phases/PhaseHelper"

export const CreateProfileDefinition = ({ group, show, showOrHide, onComplete }) => {
    const [state, setstate] = useState({
        posting: false,
        httpStatus: 200,
        status: 'pending',
        data: {
            inputRegistry: null,
        },
        input: [],
        faults: [],
    })

    const onCloseModal = async () => {
        showOrHide()

        // Reset the state on close
        setTimeout(() => {
            setstate((prev: any) => ({
                ...prev,
                posting: false,
                httpStatus: 200,
                status: 'pending',
                data: {
                    inputRegistry: null,
                },
                input: [],
                faults: [],
            }))
        }, 300)
    }

    const [phaseState, setPhaseState] = useState<PhaseState>({
        manifest: [],
        current: null,
        previous: null,
        next: null
    });
    
    useEffect(() => {
        if (show) profileDefinitionInitialization()
    }, [show])

    const profileDefinitionInitialization = async () => {
        let { status, data, httpStatus } = state

        // Generate initial input & error buckets
        // This creates the { [slug]: {} } structure for every phase
        const initialInput: any = [];
        const initialFaults: any = [];

        try {
            const [manifestRes]: any[] = await Promise.all([
                HttpServices.httpGet(PEOPLE_PROFILE.DEFINIT_MANIFEST),
            ]);

            if (manifestRes.status === 200) {
                const phaseManifest: PhaseManifest[] = manifestRes.data.manifest.map((manifest: any, index: number) => {
                    // Initialize the buckets for each specific slug
                    const phaseInputs = {}
                    const phaseFaults = {}

                    // Loop through the elements (attr.s) to extract input IDs
                    // Assume 'g' is the input object containing the ID
                    manifest.definitions.forEach((definition: any) => {
                        if (!definition.type.includes('sub-header', 'divider')) {
                            // Resolve Default Values
                            const displayType = definition.type;
                            let defaultValue: any = generateDefaultValue(definition)

                            // Set the default value for each input ID
                            phaseInputs[definition.name] = defaultValue;
                            phaseFaults[definition.name] = '';
                        }
                    });

                    // PUSH the keyed object into the state arrays
                    initialInput.push({ [manifest.slug]: phaseInputs });
                    initialFaults.push({ [manifest.slug]: phaseFaults });

                    return ({
                        gid: generateUniqueId(manifest.slug),
                        sequence: index,
                        slug: manifest.slug,
                        title: manifest.title,
                        caption: manifest.caption,
                        module: manifest.strategy,
                    })
                });

                // Initialize the Phase State Engine
                const newPhaseState = initializePhasing(phaseManifest, phaseManifest[0]?.slug);
                setPhaseState(newPhaseState)

                httpStatus = 200
                status = 'fulfilled'

                // Add the raw manifest into state
                data.inputRegistry = manifestRes.data.manifest
            } else {
                httpStatus = 500
                status = 'rejected'
            }
        } catch (error) {
            httpStatus = 500
            status = 'rejected'
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            input: initialInput,
            faults: initialFaults,
            data: {
                ...prevState.data,
                ...data,
            },
        }));
    }

    const onChangeHandler = (e: any) => {
        let { current } = phaseState
        let { posting, input } = state

        if (posting) return

        const phaseSlug = current.slug
        const { name, value } = e.target;

        let currentValue = lookupElement(name, phaseSlug, input);
        let output: any = G_onInputChangeHandler(e)

        if (typeof currentValue === "number") {
            output.value = value === "" || isNaN(Number(value)) ? 0 : Number(value);
        }

        setstate(prev => ({
            ...prev,
            input: modifyElement(name, phaseSlug, output.value, prev.input),
            faults: modifyElement(name, phaseSlug, output.error, prev.faults)
        }));
    }

    const onInputBlur = (e: any, currentPhase: any) => {
        let { posting, data } = state
        if (posting) return

        const { name } = e.target;
        const phaseSlug = currentPhase.slug
        const whichInput = inputRegistryLookup(data.inputRegistry, name);

        if (!whichInput) {
            console.error(`[Schema Error]: Object Not Found: ${name}`) // Object Not Found: 
            return;
        }

        let output: any = G_ValidationLogic(e, whichInput.val, whichInput.title, whichInput?.min, whichInput?.max)

        setstate(prev => ({
            ...prev,
            input: modifyElement(name, phaseSlug, output.value, prev.input),
            faults: modifyElement(name, phaseSlug, output.error, prev.faults)
        }));
    }

    const onListBoxHandler = (e: any, xid: string, currentPhase: any) => {
        if (state.posting) return

        const phaseSlug = currentPhase.slug

        setstate(prev => ({
            ...prev,
            input: modifyElement(xid, phaseSlug, e, prev.input),
            faults: modifyElement(xid, phaseSlug, '', prev.faults)
        }));
    }

    const toggleSwitchHandler = (e: any, currentPhase: any) => {
        let { posting, data } = state
        if (posting) return

        const { name, checked } = e.target;
        const phaseSlug = currentPhase.slug
        const whichInput = inputRegistryLookup(data.inputRegistry, name);

        if (!whichInput || !whichInput.toggles) {
            console.error(`[Schema Error]: Toggle logic for '${name}' failed.`);
            return;
        }

        const resolvedValue = whichInput.toggles[checked ? 'true' : 'false'].key

        setstate(prev => ({
            ...prev,
            input: modifyElement(name, phaseSlug, resolvedValue, prev.input),
            faults: modifyElement(name, phaseSlug, '', prev.faults)
        }));
    }

    /**
     * Serves as the viewport for resolving the PhaseManifest module type and renders either a 
     * static component or a dynamic component.
     * 
     * @param {string} currentPhase - The unique identifier for the phase (e.g., 'pm_identity_access').
     */
    const phaseProjection = (currentPhase: any) => {
        if (!currentPhase) return null
        let { input, faults, data } = state

        // Call the helper to get the "active" bucket
        const currentValues = getPhaseBucket(currentPhase.slug, input);
        const currentFaults = getPhaseBucket(currentPhase.slug, faults);
        const phaseDefinitions = getPhaseDefinitions(data.inputRegistry, currentPhase.slug)

        if (!phaseDefinitions) {
            console.error(`Error: No render engine found for slug "${currentPhase.title}"`);

            return <PhaseErrorState
                message={`Error: No render found for "${currentPhase.title}"`}
            />
        }

        return <ProfileDefinitionPhaser
            state={state}
            setstate={setstate}
            phase={currentPhase}
            values={currentValues}
            faults={currentFaults}
            blurHandler={onInputBlur}
            definitions={phaseDefinitions}
            manifest={phaseState.manifest}
            changeHandler={onChangeHandler}
            listboxHandler={onListBoxHandler}
            toggleHandler={toggleSwitchHandler}
        />
    };

    const goToPhaseHandler = (goToSlug: any) => {
        const { posting } = state
        const { manifest } = phaseState

        if (posting) return

        const phaseIndex = manifest.findIndex((p: any) => p.slug === goToSlug);
        const current = manifest[phaseIndex] || manifest[0];

        // Resolve Previous/Next references
        const prevPhase = manifest[phaseIndex - 1];
        const nextPhase = manifest[phaseIndex + 1];

        setPhaseState((prevPhaseState: any) => ({
            ...prevPhaseState,
            current,
            previous: prevPhase ? { sequence: prevPhase.sequence, slug: prevPhase.slug } : null,
            next: nextPhase ? { sequence: nextPhase.sequence, slug: nextPhase.slug } : null
        }))
    }

    const previousPhaseHandler = () => {
        const { previous } = phaseState;
        if (state.posting) return

        if (previous) {
            goToPhaseHandler(previous.slug);
        }
    }

    const nextPhaseHandler = async (e: any) => {
        e.preventDefault()

        let { input, posting, data } = state
        let hasValidationError = false
        const { current, next } = phaseState;

        if (posting || !current) return

        // Identify all fields in the current phase that require validation
        const values = getPhaseBucket(current.slug, input);
        const phaseDefinitions = getPhaseDefinitions(data.inputRegistry, current.slug);

        // 1. Identify all fields using the primitive 'type' keys
        const inputsToValidate = phaseDefinitions.filter(item =>
            ['text', 'numeric', 'telephone', 'predefined', 'reference', 'date', 'file', 'toggle'].includes(item.type)
        );

        inputsToValidate.forEach((objectRec: any) => {
            try {
                let error = '';
                const currentValue = values[objectRec.name];

                // Base mandatory requirement
                let isMandatory = objectRec?.mand ?? false

                // Logic Bridge: If not mandatory, check the 'Mandates' ruleset
                if (!isMandatory && objectRec?.mandates) {
                    /** 
                     * Reuse the same logic engine used for 'resolveManifestLogic'.
                     * If the rules pass (e.g., boundaries are 'enforced'), 'isMandatory' flips to true.
                     */
                    isMandatory = resolveManifestLogic(objectRec.mandates, values);
                }

                /**
                 * PRIORITY CHECK: Data Integrity Check (Format & Pattern)
                 * Run the Global Validation Logic first. If the user provided data, we check if it's "Valid".
                 * This applies primarily to Text, Numeric, and Telephone.
                 */
                const hasContent = currentValue !== null && currentValue !== undefined && currentValue !== '';

                if (['text', 'numeric', 'telephone'].includes(objectRec.type)) {
                    const syntheticEvent = {
                        target: {
                            name: objectRec.name,
                            required: isMandatory,
                            // Treat numeric 0 as valid content, but trim strings
                            value: typeof currentValue === 'string' ? currentValue.trim() : currentValue,
                        }
                    };

                    const result = G_ValidationLogic(syntheticEvent, objectRec.val, objectRec.title, objectRec?.min, objectRec?.max);

                    if (result.error) {
                        error = result.error; // Capture format/pattern error first
                    }
                }

                /**
                  * SECONDARY CHECK: Mandatory Requirement Check (Empty Check)
                  * If there is no Pattern error, but the field is mandatory and empty, 
                  * trigger the "Selection Required" error.
                  */
                if (!error && isMandatory && !hasContent) {
                    switch (objectRec.type) {
                        case 'predefined':
                        case 'reference':
                        case 'file':
                            // Collections/Arrays check
                            if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
                                error = `${objectRec.title} requires a selection.`;
                            }
                            break;

                        case 'date':
                        case 'toggle':
                            error = `Please provide a valid ${objectRec.title.toLowerCase()}.`;
                            break;

                        default:
                            // Fallback for standard text/numeric empty state
                            error = `${objectRec.title} is required.`;
                            break;
                    }
                }

                // State Reconciliation: Update Faults
                if (error) {
                    hasValidationError = true;

                    setstate(prev => ({
                        ...prev,
                        faults: modifyElement(objectRec.name, current.slug, error, prev.faults)
                    }));
                }
            } catch (err) {
                console.error(`[Schema Validation Error]: ${objectRec.name}`, err);
            }
        });

        if (hasValidationError) {
            // toast.error("Please complete all required fields.");
            return;
        }

        // Move to Next phase OR submit the form
        if (next) {
            /**
             * We are not at the end. 
             * Logic here: Update the URL or State to the next slug.
             */
            goToPhaseHandler(next.slug);
        } else {
            /**
             * No 'next' phase exists in the registry.
             * Logic here: Trigger the final API submission.
             */
            setstate((prev: any) => ({
                ...prev,
                posting: true,
            }))

            profileDefinitionHandler()
        }
    }

    const profileDefinitionHandler = async () => {
        let { input, faults, httpStatus } = state

        try {
            let formData = new FormData()
            formData.append('group', group.g)

            // Serialize Input Configuration (The JSON Blob)
            const identityBlob = input[0].identity_and_access;
            const configurationBlob = input[1].input_configuration;

            formData.append('identity', JSON.stringify(identityBlob))
            formData.append('configuration', JSON.stringify(configurationBlob))

            const response: any = await HttpServices.httpPost(PEOPLE_PROFILE.CREATE_DEFINITION, formData)
            httpStatus = response.status

            if (httpStatus === 200) {
                onCloseModal()
                onComplete()
            } else {
                if (httpStatus === 422) {
                    Object.entries(response.data).forEach(([field, errorMessages]) => {
                        const submissionErrors = {
                            ...faults,
                            [field]: errorMessages
                        };

                        setstate((prev: any) => ({
                            ...prev,
                            errors: submissionErrors,
                        }));
                    });
                } else {
                    toast.error(APPLICATION.ERR_MSG);
                }
            }
        } catch (error) {
            toast.error(APPLICATION.ERR_MSG);
        }
    }

    return (
        <React.Fragment>
            <SplitScreenModal
                show={show}
                status={state.status}
                posting={state.posting}
                showOrHide={onCloseModal}
                // onSubmit={nextPhaseHandler}
                formHandler={nextPhaseHandler}
                formAction={
                    phaseState.next === null ? "Create" : "Next"
                }
                // size={
                //     state.status === 'fulfilled' ? "3xl"
                //         : state.status === 'rejected' ? "md"
                //             : "sm"
                // }
                phase={({
                    prevHandler: previousPhaseHandler,
                    prev: phaseState.previous,
                    next: phaseState.next
                })}
                header={
                    <>
                        {/* <div className="w-full flex flex-col h-full gap-y-2.5">
                            <div className="flex md:pb-0 pb-2 md:pt-2 pt-4 flex-row-reverse align-middle items-center">
                                <button type="button" className="rounded-md md:hidden text-gray-800 dark:text-gray-200 hover:text-gray-800 focus:outline-none flex flex-row align-middle pr-3"
                                    onClick={onCloseModal}>
                                    <span className="fas fa-times border-none text-lg"></span>
                                    <span className="sr-only text-sm ml-3">Close panel</span>
                                </button>

                                <div className="flex-grow">
                                    <h2 className="md:text-3xl text-2xl font-medium tracking-wider text-orange-600 dark:text-white md:text-orange-600 dark:md:text-white">
                                        New Profile<br className="hidden md:block" />Attribute
                                    </h2>
                                </div>
                            </div>

                            <div className="sm:col-span-8 flex-grow lg:col-span-7 mb-3 flex flex-col gap-y-3 text-primary dark:text-slate-100 md:text-slate-600 dark:md:text-slate-400">
                                <span className="block md:py-2 text-sm">
                                    Define properties, validation rules, and visibility settings for your profile attribute
                                </span>
                            </div>
                        </div>
 */}

                        <span className="block text-xl md:text-2xl">
                            <h2 className="md:text-3xl text-2xl font-medium tracking-wider text-orange-600 dark:text-white md:text-orange-600 dark:md:text-white">
                                New Profile<br className="hidden md:block" />Attribute
                            </h2>

                            <span className="text-sm pt-4 pb-2 text-stone-600 block">
                                Define properties, validation rules, and visibility settings for your profile attribute
                            </span>
                        </span>

                        <PhaseBridge
                            phaseState={phaseState}
                        />
                    </>
                }
                contents={
                    state.status === 'fulfilled' && (
                        <div className="w-full h-full flex flex-col md:px-5 px-2 flex-1">
                            {
                                phaseProjection(phaseState.current)
                            }
                        </div>
                    )
                }
            />
        </React.Fragment>
    )
}